import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ToastService } from '@teorema/patron';
import { Task } from 'src/app/models';

@Component({
  selector: 'app-task-bulk-update',
  templateUrl: './task-bulk-update.component.html',
  standalone: false,
})
export class TaskBulkUpdateComponent implements OnInit, OnChanges {
  @Output() confirm: EventEmitter<any> = new EventEmitter();
  @Input() visible = false;
  @Input() tasks: Task[] = [];

  saving?: boolean;
  formGroup!: FormGroup;
  msgs: string[] = [];
  visibleConfirm = false;
  selectedTaskIds: Set<string> = new Set();

  constructor(
    private formBuilder: FormBuilder,
    private toastService: ToastService
  ) {
    this.formGroup = this.formBuilder.group({
      status: [null],
      expectedDateString: [null],
      removeExpectedDate: [false],
      developer: [null],
    });
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    // Quando o modal abre (visible muda para true), limpa a seleção
    if (changes['visible'] && changes['visible'].currentValue === true) {
      this.selectedTaskIds = new Set();
    }
  }

  get selectedCount(): number {
    return this.selectedTaskIds.size;
  }

  get allSelected(): boolean {
    return this.tasks.length > 0 && this.selectedTaskIds.size === this.tasks.length;
  }

  get someSelected(): boolean {
    return this.selectedTaskIds.size > 0 && !this.allSelected;
  }

  isSelected(taskId: string): boolean {
    return this.selectedTaskIds.has(taskId);
  }

  toggleTaskSelection(taskId: string): void {
    const updated = new Set(this.selectedTaskIds);
    if (updated.has(taskId)) {
      updated.delete(taskId);
    } else {
      updated.add(taskId);
    }
    this.selectedTaskIds = updated;
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedTaskIds = new Set();
    } else {
      this.selectedTaskIds = new Set(this.tasks.map(t => t.id));
    }
  }

  getTaskLabel(task: Task): string {
    return task.summary || task.description?.substring(0, 40) || 'Sem descrição';
  }

  onRemoveExpectedDateChange(): void {
    const dateControl = this.formGroup.get('expectedDateString');
    if (this.formGroup.get('removeExpectedDate')?.value) {
      dateControl?.setValue(null);
      dateControl?.disable();
    } else {
      dateControl?.enable();
    }
  }

  runBulkUpdate() {
    if (this.selectedCount === 0) {
      this.toastService.show({
        color: 'warn',
        message: 'Selecione ao menos uma tarefa para atualizar',
      });
      return;
    }

    this.msgs = [];
    if (this.formGroup.get('status')?.value) {
      this.msgs.push(
        'o status para ' + this.formGroup.get('status')?.value.description
      );
    }
    if (this.formGroup.get('removeExpectedDate')?.value) {
      this.msgs.push('remover a previsão');
    } else if (this.formGroup.get('expectedDateString')?.value) {
      let temp = this.formGroup.get('expectedDateString')?.value.split('-');
      this.msgs.push(
        'a previsão para ' + temp[2] + '/' + temp[1] + '/' + temp[0]
      );
    }
    if (this.formGroup.get('developer')?.value) {
      this.msgs.push(
        'o desenvolvedor para ' + this.formGroup.get('developer')?.value._name
      );
    }
    if (!this.msgs.length) {
      this.toastService.show({
        color: 'warn',
        message: 'Selecione ao menos uma alteração',
      });
      return;
    }

    this.confirmSwitch();
  }

  messageArrayToString(msgs?: string[]): string {
    let delimiter = ', ';
    let msg = msgs!.join(delimiter);
    let index = msg.lastIndexOf(delimiter);

    if (index > -1) {
      msg = msg.substring(0, index) + ' e ' + msg.substring(index + delimiter.length);
    }

    return msg;
  }

  confirmSwitch() {
    this.visibleConfirm = false;

    setTimeout(() => {
      this.visibleConfirm = true;
    }, 1);
  }

  _confirm() {
    this.visibleConfirm = false;

    this.confirm.emit({
      status: this.formGroup.get('status')?.value,
      expectedDateString: this.formGroup.get('expectedDateString')?.value,
      removeExpectedDate: this.formGroup.get('removeExpectedDate')?.value,
      user: this.formGroup.get('developer')?.value,
      selectedTaskIds: this.selectedTaskIds,
    });

    this.close();
  }

  getStatusClass(description?: string): string {
    if (!description) return 'secondary';
    const d = description.toLowerCase();
    if (d.includes('conclu') || d.includes('finaliz') || d.includes('done')) return 'success';
    if (d.includes('andamento') || d.includes('progress') || d.includes('execu')) return 'primary';
    if (d.includes('bloqueado') || d.includes('impedido') || d.includes('block')) return 'danger';
    if (d.includes('revisão') || d.includes('review') || d.includes('aguard')) return 'warn';
    return 'secondary';
  }

  close() {
    this.clearForm();
    this.visible = !this.visible;
  }

  clearForm() {
    this.formGroup.get('status')?.setValue(null);
    this.formGroup.get('expectedDateString')?.setValue(null);
    this.formGroup.get('expectedDateString')?.enable();
    this.formGroup.get('removeExpectedDate')?.setValue(false);
    this.formGroup.get('developer')?.setValue(null);
    this.selectedTaskIds = new Set();
  }
}
